import { 
  validateForm, 
  validateZipCode, 
  handleDobValidation,
  validatePhoneNumber,
  validateEmail,
  handleFocus
} from "./form-validation";

declare const grecaptcha: any;

class TreatmentRequestFormHandler {
  private form: HTMLFormElement | null;
  private submitButton: HTMLButtonElement | null;
  private fillIn: HTMLElement | null;
  private arrivalDate: HTMLInputElement | null;
  private departureDate: HTMLInputElement | null;
  private treatmentFirstDate: HTMLInputElement | null;
  private treatmentLastDate: HTMLInputElement | null;
  private dateOfBirth: HTMLInputElement | null;
  private destinationPhoneNumber: HTMLInputElement | null;
  private patientPhoneNumber: HTMLInputElement | null;
  private homeFacilityPhoneNumber: HTMLInputElement | null;
  private homeFacilityFax: HTMLInputElement | null;
  private destinationZipCode: HTMLInputElement | null;
  private patientZipCode: HTMLInputElement | null;
  private contactEmail: HTMLInputElement | null;
  private patientEmail: HTMLInputElement | null;
  private loadingSpinner: HTMLElement | null;

  // multi-step form state
  private steps: HTMLElement[] = [];
  private indicators: HTMLElement[] = [];
  private progressFill: HTMLElement | null = null;
  private stepLabelEl: HTMLElement | null = null;
  private btnBack: HTMLButtonElement | null = null;
  private btnNext: HTMLButtonElement | null = null;
  private btnCancel: HTMLButtonElement | null = null;
  private formStepInput: HTMLInputElement | null = null;
  private reviewSummary: HTMLElement | null = null;
  private currentStepIndex = 0;

  constructor(formId: string, submitButtonId: string) {
    this.form = document.getElementById(formId) as HTMLFormElement | null;
    this.submitButton = document.getElementById(submitButtonId) as HTMLButtonElement | null;
    this.fillIn = document.getElementById("fill-in") ?? null;
    this.arrivalDate = document.getElementById('arrivalDate') as HTMLInputElement | null;
    this.departureDate = document.getElementById('departureDate') as HTMLInputElement | null;
    this.treatmentFirstDate = document.getElementById('treatmentFirstDate') as HTMLInputElement | null;
    this.treatmentLastDate = document.getElementById('treatmentLastDate') as HTMLInputElement | null;
    this.dateOfBirth = document.getElementById('dateOfBirth') as HTMLInputElement | null;
    this.destinationPhoneNumber = document.getElementById("destPhone") as HTMLInputElement | null;
    this.patientPhoneNumber = document.getElementById("patientPhoneNumber") as HTMLInputElement | null;
    this.homeFacilityPhoneNumber = document.getElementById("homeFacPhone") as HTMLInputElement | null;
    this.homeFacilityFax = document.getElementById("homeFacFax") as HTMLInputElement | null;
    this.destinationZipCode = document.getElementById("destZipCode") as HTMLInputElement | null;
    this.patientZipCode = document.getElementById("zipCode") as HTMLInputElement | null;
    this.contactEmail = document.getElementById("contactEmail") as HTMLInputElement | null;
    this.patientEmail = document.getElementById("email") as HTMLInputElement | null;
    this.loadingSpinner = document.getElementById("loadingSpinner") as HTMLElement | null;

    // initialize multi-step UI elements (DOM should be present when constructor is called)
    this.steps = Array.from((this.form?.querySelectorAll<HTMLElement>('.form-step') ?? []));
    this.indicators = Array.from(document.querySelectorAll<HTMLElement>('.step-indicator'));
    this.progressFill = document.getElementById('progressFill');
    this.stepLabelEl = document.getElementById('stepLabel');
    this.btnBack = document.getElementById('btnBack') as HTMLButtonElement | null;
    this.btnNext = document.getElementById('btnNext') as HTMLButtonElement | null;
    this.btnCancel = document.getElementById('btnCancel') as HTMLButtonElement | null;
    this.formStepInput = document.getElementById('formStep') as HTMLInputElement | null;
    this.reviewSummary = document.getElementById('reviewSummary');

  }

  private isValidDate(dateString: string): boolean {
    // Check format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false;
  
    const [year, month, day] = dateString.split("-").map(Number);
  
    const inputDate = new Date(year, month - 1, day);
    if (isNaN(inputDate.getTime())) return false;
  
    if (
      inputDate.getFullYear() !== year ||
      inputDate.getMonth() !== month - 1 ||
      inputDate.getDate() !== day
    ) {
      return false;
    }
  
    return true;
  }
  
  private parseDateAsUTC(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }

  private validateDateRange(
    arrivalDate: string,
    departureDate: string,
    firstTreatmentDate: string,
    lastTreatmentDate: string
  ): boolean {
    let isValid = true;
  
    const arrival = this.parseDateAsUTC(arrivalDate);
    const departure = this.parseDateAsUTC(departureDate);
    const firstTreatment = this.parseDateAsUTC(firstTreatmentDate);
    const lastTreatment = this.parseDateAsUTC(lastTreatmentDate);
  
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
  
    const isDateValidForArrival = (date: Date) => {
      const minArrivalDate = new Date(today);
      minArrivalDate.setUTCDate(today.getUTCDate() + 7);
      return date >= minArrivalDate;
    };
  
    const validateDateField = (
      input: HTMLInputElement,
      checks: { condition: boolean; message: string }[]
    ) => {
      for (const { condition, message } of checks) {
        if (condition) {
          isValid = false;
          return this.showError(input, message);
        }
      }
    
      this.hideError(input);
    };
    
    if (arrivalDate.length > 0) {
      validateDateField(this.arrivalDate!, [
        { condition: !this.isValidDate(arrivalDate), message: "Please enter a valid Arrival Date." },
        {
          condition: !isDateValidForArrival(arrival),
          message: "Arrival Date cannot be less than 7 days from today.",
        },
      ]);
    }
    
    if (departureDate.length > 0) {
      validateDateField(this.departureDate!, [
        { condition: !this.isValidDate(departureDate), message: "Please enter a valid Departure Date." },
        {
          condition: departure < arrival || departure < firstTreatment || departure < lastTreatment,
          message: "The Departure Date must be after your Arrival Date<br/> and First Requested Treatment Date.",
        },
      ]);
    }
    
    if (firstTreatmentDate.length > 0) {
      validateDateField(this.treatmentFirstDate!, [
        {
          condition: !this.isValidDate(firstTreatmentDate),
          message: "Please enter a valid First Requested Treatment Date.",
        },
        {
          condition:
            !isDateValidForArrival(firstTreatment) ||
            firstTreatment < arrival ||
            firstTreatment > departure ||
            firstTreatment > lastTreatment,
          message:
            "The First Requested Date must be at least 7 days out,<br/> must be on or after your Arrival Date<br/> and before your Departure and Last Treatment Date.",
        },
      ]);
    }
    
    if (lastTreatmentDate.length > 0) {
      validateDateField(this.treatmentLastDate!, [
        {
          condition: !this.isValidDate(lastTreatmentDate),
          message: "Please enter a valid Last Requested Treatment Date.",
        },
        {
          condition: firstTreatment > lastTreatment,
          message:
            "Please make sure your Last Treatment Date<br/> is on or after First Requested Treatment Date.",
        },
        {
          condition: lastTreatment < arrival || lastTreatment > departure,
          message:
            "The Last Treatment Date must be on or after your<br/> Arrival Date, and on or before the Departure Date.",
        },
      ]);
    }    
  
    return isValid;
  }  

  private showError(input: HTMLInputElement, message: string) {
    let errorMsg = document.getElementById(input.id + "Error") as HTMLElement;

    errorMsg.innerHTML = `Error: ${message}`;
    errorMsg.classList.remove("hidden");
    input.focus();
  }

  private hideError(input: HTMLInputElement) {
    let errorMsg = document.getElementById(input.id + "Error") as HTMLElement;
    if (errorMsg) {
      errorMsg.classList.add("hidden");
    }
  }

  private validateField(target: HTMLInputElement) {
    const selectedDate = target.value;

    if (!this.isValidDate(selectedDate) && selectedDate.length > 0) {
      this.showError(target, target.getAttribute("data-msg-invalid") || "Invalid date");
      return;
    }

    this.hideError(target);

    this.validateDateRange(
      this.arrivalDate!.value,
      this.departureDate!.value,
      this.treatmentFirstDate!.value,
      this.treatmentLastDate!.value);
  }

  private configureDateInput(dateInput: HTMLInputElement | null) {
    if (dateInput) {
      // Set minimum date to 7 days from today
      const today = new Date();
      today.setDate(today.getDate() + 7);
      dateInput.min = today.toISOString().split("T")[0];

      dateInput.addEventListener("focusout", (event) => this.validateField(event.target as HTMLInputElement));
    }
  }
  
  private escapeHtml(s: string) {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
  }

  private updateProgress(i: number) {
    const total = this.steps.length;
    const activeStep = i + 1; // 1-based
    if (this.stepLabelEl) this.stepLabelEl.textContent = `Step ${activeStep} of ${total}`;
    if (this.progressFill) {
      const pct = (activeStep / total) * 100;
      this.progressFill.style.width = `${pct}%`;
    }
    if (this.formStepInput) this.formStepInput.value = String(activeStep);
    if (this.btnBack) this.btnBack.style.display = i === 0 ? 'none' : '';
    if (this.btnNext) this.btnNext.style.display = i === this.steps.length - 1 ? 'none' : '';
    if (this.submitButton) this.submitButton.style.display = i === this.steps.length - 1 ? '' : 'none';
  }

  private showStep(i: number) {
    this.steps.forEach((s, idx) => {
      if (idx === i) {
        s.classList.add('is-active');
        s.removeAttribute('aria-hidden');
      } else {
        s.classList.remove('is-active');
        s.setAttribute('aria-hidden', 'true');
      }
    });

    // focus first input in current step
    const first = this.steps[i].querySelector<HTMLElement>('input,select,textarea,button,[tabindex]:not([tabindex="-1"])');
    if (first) {
      first.focus();
    }

    this.updateProgress(i);

    // if we're on the last step, build review summary
    if (i === this.steps.length - 1) this.buildReview();
  }

  private canAdvance(fromStep: HTMLElement) {
    const inputs = Array.from(fromStep.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input,select,textarea'));
    for (const input of inputs) {
      // ignore disabled or hidden elements
      if ((input as HTMLElement).offsetParent === null) continue;
      if (!input.checkValidity()) {
        if (typeof (input as HTMLInputElement).reportValidity === 'function') {
          (input as HTMLInputElement).reportValidity();
        }
        return false;
      }
    }
    return true;
  }
  
  private buildReview() {
    if (!this.reviewSummary || !this.form) return;
    const fd = new FormData(this.form);
    const values: [string, string][] = [
      ['Name', `${fd.get('firstname') || ''} ${fd.get('lastname') || ''}`.trim()],
      ['DOB', (fd.get('dateofbirth') || '').toString()],
      ['Gender', (fd.get('gender') || '').toString()],
      ['Phone', (fd.get('phone') || '').toString()],
      ['Email', (fd.get('email') || '').toString()],
      ['Arrival', (fd.get('arrivaldate') || '').toString()],
      ['Departure', (fd.get('departuredate') || '').toString()],
      ['Purpose', (fd.get('purpose') || '').toString()],
      ['Frequency', (fd.get('frequency') || '').toString()],
      ['Insurance', (fd.get('insurance') || '').toString()],
      ['Policy', (fd.get('policy') || '').toString()],
      ['Notes', (fd.get('notes') || '').toString()],
      ['Facility notes', (fd.get('facilityNotes') || '').toString()],
    ];

    this.reviewSummary.innerHTML = values
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `<div><strong>${this.escapeHtml(k)}:</strong> ${this.escapeHtml(v)}</div>`)
      .join('');
  }

  public init() {
    if (this.form && this.submitButton) {
      console.log("Initializing Treatment Request Form Handler");
      this.bindEvents();
    }
  }

  private bindEvents() {
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = "none";
    }
    // wire multi-step buttons
    this.btnNext?.addEventListener('click', (e) => {
      e.preventDefault();
      const current = this.steps[this.currentStepIndex];
      if (!current || !this.canAdvance(current)) return;
      this.currentStepIndex = Math.min(this.steps.length - 1, this.currentStepIndex + 1);
      this.showStep(this.currentStepIndex);
    });

    this.btnBack?.addEventListener('click', (e) => {
      e.preventDefault();
      this.currentStepIndex = Math.max(0, this.currentStepIndex - 1);
      this.showStep(this.currentStepIndex);
    });

    this.btnCancel?.addEventListener('click', (e) => {
      e.preventDefault();
      if (confirm('Cancel request and go back?')) history.back();
    });

    // ensure full-form submit switches to step with first invalid field
    this.form?.addEventListener('submit', (e) => {
      if (!this.form!.checkValidity()) {
        e.preventDefault();
        const firstInvalid = this.form!.querySelector<HTMLElement>(':invalid');
        if (firstInvalid) {
          const parentStep = firstInvalid.closest<HTMLElement>('.form-step');
          if (parentStep) {
            const stepIndex = this.steps.indexOf(parentStep);
            if (stepIndex >= 0) {
              this.currentStepIndex = stepIndex;
              this.showStep(this.currentStepIndex);
              (firstInvalid as HTMLInputElement).reportValidity?.();
            }
          }
        }
      } else {
        if (this.formStepInput) this.formStepInput.value = String(this.steps.length);
      }
    }, true);

    // Set up date fields and event listener
    this.configureDateInput(this.arrivalDate);
    this.configureDateInput(this.departureDate);
    this.configureDateInput(this.treatmentFirstDate);
    this.configureDateInput(this.treatmentLastDate);

    this.submitButton?.addEventListener("click", (event) => this.handleFormSubmission(event));
    this.form?.addEventListener("input", (event) => handleFocus(event as FocusEvent)); // Assuming handleFocus is defined elsewhere

    // fill-in the form with dummy data
    this.fillIn?.addEventListener("click", (event) => {
      this.fillInForm(event);
    });

    this.dateOfBirth?.addEventListener('focusout', (event) => {
      handleDobValidation(this.dateOfBirth!);
    })


    this.arrivalDate?.addEventListener("focusout", () => this.validateField(this.arrivalDate!));

    this.departureDate?.addEventListener("focusout", () => this.validateField(this.departureDate!));

    this.treatmentFirstDate?.addEventListener("focusout", () => this.validateField(this.treatmentFirstDate!));

    this.treatmentLastDate?.addEventListener("focusout", () => this.validateField(this.treatmentLastDate!));

    this.destinationPhoneNumber?.addEventListener("focusout", () => validatePhoneNumber(this.destinationPhoneNumber!));

    this.patientPhoneNumber?.addEventListener("focusout", () => validatePhoneNumber(this.patientPhoneNumber!));

    this.homeFacilityPhoneNumber?.addEventListener("focusout", () => validatePhoneNumber(this.homeFacilityPhoneNumber!));

    this.homeFacilityFax?.addEventListener("focusout", () => validatePhoneNumber(this.homeFacilityFax!));

    this.destinationZipCode?.addEventListener("focusout", () => validateZipCode(this.destinationZipCode!));

    this.patientZipCode?.addEventListener("focusout", () => validateZipCode(this.patientZipCode!));

    this.contactEmail?.addEventListener("focusout", () => validateEmail(this.contactEmail!));

    this.patientEmail?.addEventListener("focusout", () => validateEmail(this.patientEmail!));

    // initialize step UI
    const initial = this.steps.findIndex(s => s.classList.contains('is-active'));
    this.currentStepIndex = initial >= 0 ? initial : 0;
    this.updateProgress(this.currentStepIndex);
  }

  private fillInForm(event: Event) {
    // Fill in the form with dummy data
    (document.getElementById("arrivalDate") as HTMLInputElement).value = "2027-05-01";
    (document.getElementById("treatmentFirstDate") as HTMLInputElement).value = "2027-06-01";
    (document.getElementById("treatmentLastDate") as HTMLInputElement).value = "2027-06-15";
    (document.getElementById("departureDate") as HTMLInputElement).value = "2027-06-25";
    (document.getElementById("totalTreatments") as HTMLInputElement).value = "5";

    const purposeOfTravel = document.getElementById("purposeOfTravel") as HTMLSelectElement;
    if (purposeOfTravel) purposeOfTravel.value = "Vacation"; // Set value for dropdown
    const destState = document.getElementById("destState") as HTMLSelectElement;
    if (destState) destState.value = "CO"; // Set value for dropdown
    (document.getElementById("destZipCode") as HTMLInputElement).value = "80202";
    (document.getElementById("destPhone") as HTMLInputElement).value = "303-555-1212";

    (document.getElementById("contactEmail") as HTMLSelectElement).value = "test@test.com";
    (document.getElementById("firstName") as HTMLSelectElement).value = "Test";
    (document.getElementById("lastName") as HTMLSelectElement).value = "Tester";
    (document.getElementById("dateOfBirth") as HTMLTextAreaElement).value = "1950-01-01";
    (document.getElementById("zipCode") as HTMLInputElement).value = "12345";
    (document.getElementById("patientPhoneNumber") as HTMLInputElement).value = "3102223333";
    (document.getElementById("email") as HTMLInputElement).value = "test2@test.com";

    const homeFacState = document.getElementById("homeFacState") as HTMLSelectElement;
    if (homeFacState) homeFacState.value = "CO"; // Set value for dropdown

    const davitaPatient = document.getElementById("davitaPatient") as HTMLSelectElement;
    if (davitaPatient) davitaPatient.value = "Yes"; // Set value for dropdown

    const distanceWillTravel = document.getElementById("distanceWillTravel") as HTMLSelectElement;
    if (distanceWillTravel) distanceWillTravel.value = "20 miles"; // Set value for dropdown

    (document.getElementById("homeFacName") as HTMLInputElement).value = "Independence Dialysis";
    (document.getElementById("homeFacCity") as HTMLInputElement).value = "Loveland";
    (document.getElementById("homeFacPhone") as HTMLInputElement).value = "303-333-2222";
    (document.getElementById("homeFacFax") as HTMLInputElement).value = "303-777-2222";
    (document.getElementById("homeFacContact") as HTMLInputElement).value = "Miss Betty";

    const accessType = document.getElementById("accessType") as HTMLSelectElement;
    if (accessType) accessType.value = "Catheter"; // Set value for dropdown

    const treatmentSchedule = document.getElementById("treatmentSchedule") as HTMLSelectElement;
    if (treatmentSchedule) treatmentSchedule.value = "MWF"; // Set value for dropdown

    const treatmentScheduleFlexible = document.getElementById("treatmentScheduleFlexible") as HTMLSelectElement;
    if (treatmentScheduleFlexible) treatmentScheduleFlexible.value = "Days only"; // Set value for dropdown

    const isolationRequired = document.getElementById("isolationRequired") as HTMLSelectElement;
    if (isolationRequired) isolationRequired.value = "Yes"; // Set value for dropdown

    const treatmentLengthHrs = document.getElementById("treatmentLengthHrs") as HTMLSelectElement;
    if (treatmentLengthHrs) treatmentLengthHrs.value = "5"; // Set value for dropdown

    const treatmentLengthMin = document.getElementById("treatmentLengthMin") as HTMLSelectElement;
    if (treatmentLengthMin) treatmentLengthMin.value = "15"; // Set value for dropdown

    const primaryInsuranceType = document.getElementById("primaryInsuranceType") as HTMLSelectElement;
    if (primaryInsuranceType) primaryInsuranceType.value = "Private Pay"; // Set value for dropdown

    (document.getElementById("primaryInsuranceName") as HTMLInputElement).value = "Senior Care";
    (document.getElementById("secondaryInsuranceName") as HTMLInputElement).value = "Secondary Care";
    (document.getElementById("additionalInfo") as HTMLInputElement).value = "Additional Info";

    // this.submitButton!.disabled = false;
  }

  private async handleFormSubmission(event: Event) {
    event.preventDefault();
  
    if (this.form) {
      this.submitButton!.disabled = true;
      if (this.loadingSpinner) {
        this.loadingSpinner.style.display = "block";
      }

      const isValid = await validateForm(this.form, [
        () => this.validateDateRange(
          this.arrivalDate!.value,
          this.departureDate!.value,
          this.treatmentFirstDate!.value,
          this.treatmentLastDate!.value
        ),
        () => validatePhoneNumber(this.patientPhoneNumber!),
        () => validatePhoneNumber(this.homeFacilityPhoneNumber!),
        () => validatePhoneNumber(this.homeFacilityFax!),
        () => validateZipCode(this.destinationZipCode!),
        () => validateZipCode(this.patientZipCode!),
        () => validateEmail(this.contactEmail!),
        () => validateEmail(this.patientEmail!),
        () => handleDobValidation(this.dateOfBirth!)
      ]);
  
      if (isValid) {
        const data = this.collectFormData();
        grecaptcha.enterprise.ready(async () => {
          const token = await grecaptcha.enterprise.execute('6LfCG68sAAAAAFxWFC-PLIw_h4-hVk739gkX-52O', { action: 'treatment_request_form' });
          const response = await this.sendPostRequest("/treatment-request-form", {...data, ['g-recaptcha-response']: token});
          if (response.ok) {
            const messageContainer = document.getElementById("form-success");
            if (messageContainer) {
              // show success message
              messageContainer.classList.remove("hidden");
              const messageContent = document.getElementById("success-message-content");
              if (messageContent) {
                messageContent.innerHTML = "Thanks for contacting us! We will get in touch with you shortly."
              }
            }

            if (this.form) {
              // hide form
              this.form.style.display = "none";
            }
          } else {
            this.submitButton!.disabled = false;

            // remove the hidden class from the id apiError
            const apiError = document.getElementById("apiError");
            if (apiError) {
              apiError.style.display = "block";
              apiError.innerHTML = "Error: An error occurred while submitting the form. Please try again.";
            }
          }

          if (this.loadingSpinner) {
            this.loadingSpinner.style.display = "none";
          }
        });
      }
    }
  }

  private collectFormData(): Record<string, unknown> {
    const formData = new FormData(this.form!);
    return Object.fromEntries(formData.entries());
  }

  private async sendPostRequest(url: string, data: Record<string, unknown>) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      return response;
    } catch (error) {
      throw new Error("Error Submitting Form");
    }
  }
}

// Usage
document.addEventListener("DOMContentLoaded", () => {
  const formHandler = new TreatmentRequestFormHandler("treatment_request_form", "btnSubmit");
  formHandler.init();
});